import ChainLogo from '@components/ChainLogo'
import TokenAvatar from '@components/TokenAvatar'
import TokenPicker from '@components/TokenPicker'
import useBalances from '@plug-core/hooks/useBalances'
import useConfig from '@plug-core/hooks/useConfig'
import { PriceDifference, PriceDifferenceLevel } from '@plug-core/hooks/useSwap'
import { PlugToken } from '@plug-core/interfaces/common'
import { InputError, SwapProviderQuote } from '@plug-core/interfaces/swapInterface'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { filterDestinationTokens } from '@plug-core/utils/filterDestinationTokens'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { e8sToDecimals, sanitizeDecimalInput } from '@plug-core/utils/validationHelper'
import { Button, Field, IconButton, Input, ListGroup, ListRow, Surface, Text, cn } from '@ui'
import React, { FC, PropsWithChildren, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDown, HiOutlineSwitchVertical } from 'react-icons/hi'
import { SwapProviderSelect } from './Components/SwapProviderSelect'

export interface TokenCardProps {
  token?: PlugToken
  onTokenSelect?: (token: PlugToken | undefined) => void
  showBalance?: boolean
  amount?: string
  setAmount: (newAmount: string) => void
  maxAmount?: bigint
  tokensForSelect?: PlugToken[]
  direction: 'from' | 'to'
  disablePriceInput?: boolean
  error?: string | undefined
  disableTokenPicker?: boolean
  preselectChain?: string
  priceDifference?: PriceDifference
  tokensToBalances: (tokens: PlugToken[]) => TokenBalance[]
}

/** plug-core's price-difference severity mapped to status tokens. */
const PRICE_DIFFERENCE_TONE: Record<PriceDifferenceLevel, string> = {
  ok: 'text-success',
  caution: 'text-warning',
  danger: 'text-danger',
}

const amountChipClasses =
  'cursor-pointer rounded-xs border-0 bg-surface-2 px-2 py-0.5 font-text text-[12px] font-medium text-fg-2 transition-colors hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'

export const TokenCard = ({
  token,
  onTokenSelect,
  showBalance,
  amount,
  setAmount,
  maxAmount,
  tokensForSelect,
  direction,
  disablePriceInput,
  error,
  disableTokenPicker,
  preselectChain,
  priceDifference,
  tokensToBalances,
}: TokenCardProps): JSX.Element => {
  const { t } = useTranslation()
  const { tokenPricesWithChange } = useConfig()

  const [tokenPickerVisible, setTokenPickerVisible] = useState(false)

  const balance = (token && tokensToBalances([token])[0]?.humanBalance) || 0

  const tokenChipContent = (
    <>
      <span className="relative shrink-0">
        <TokenAvatar logo={token?.logo} size={28} />
        <span className="absolute -right-1 -bottom-1 grid place-items-center rounded-full bg-surface p-px">
          <ChainLogo chain={token?.chain?.toString()} size={12} />
        </span>
      </span>
      <span className="max-w-[120px] truncate text-[15px] font-semibold">
        {token?.name || '...'}
      </span>
    </>
  )

  const tokenChip = disableTokenPicker ? (
    <span className="flex items-center gap-2 py-1 pr-1 pl-1 text-fg">{tokenChipContent}</span>
  ) : (
    <button
      type="button"
      aria-haspopup="dialog"
      onClick={() => setTokenPickerVisible(true)}
      className="flex cursor-pointer items-center gap-2 rounded-pill border border-solid border-[var(--plug-secondary-border)] bg-[var(--plug-secondary)] py-1 pr-2 pl-1 font-text text-fg transition-colors duration-150 hover:border-[var(--plug-secondary-border-hover)] hover:bg-[var(--plug-secondary-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus active:scale-[0.98] motion-reduce:active:scale-100"
    >
      {tokenChipContent}
      <HiChevronDown aria-hidden="true" className="size-4 text-fg-2" />
    </button>
  )

  return (
    <Surface padding="md" className="flex flex-col gap-2">
      <Field error={error}>
        <Input
          size="lg"
          bare
          inputMode="decimal"
          aria-label={t(`swap.picker.token.${direction}`)}
          disabled={disablePriceInput}
          value={amount}
          placeholder="0.0"
          onChange={(e) => sanitizeDecimalInput(e.target.value, setAmount)}
          className="font-display text-[24px] font-semibold tabular disabled:cursor-default disabled:[-webkit-text-fill-color:var(--plug-text)]"
          wrapperClassName="opacity-100 pr-2"
          trailing={tokenChip}
        />
      </Field>

      <div className="flex min-h-6 flex-wrap items-center gap-x-2 gap-y-1">
        {token && tokenPricesWithChange[token.id] && (
          <Text as="span" variant="label" tone="fg-2" tabular>
            {formatMoney(tokenPricesWithChange[token.id].price * Number(amount || '0'))}
          </Text>
        )}

        {token &&
          typeof maxAmount === 'bigint' &&
          [
            { label: '50%', amount: maxAmount / 2n },
            { label: 'MAX', amount: maxAmount },
          ].map(({ amount, label }) => {
            return (
              <button
                key={label}
                type="button"
                onClick={() => setAmount(e8sToDecimals(amount, token.decimals).toString())}
                className={amountChipClasses}
              >
                {label}
              </button>
            )
          })}

        {showBalance && (
          <Text as="span" variant="label" tone="fg-2" tabular className="ml-auto">
            {t('swap.balance')}: {formatNumber(balance)}
          </Text>
        )}

        {priceDifference && (
          <Text as="span" variant="label" tone="fg-2" tabular className="ml-auto">
            {t('swap.priceDifference')}:
            <span className={PRICE_DIFFERENCE_TONE[priceDifference.level]}>
              {' '}
              {formatNumber(priceDifference.percentage, '%', {
                signDisplay: 'exceptZero',
                maximumFractionDigits: 2,
              })}
            </span>
          </Text>
        )}
      </div>

      {!disableTokenPicker && (
        <TokenPicker
          visible={tokenPickerVisible}
          title={t(`swap.picker.token.${direction}`)}
          tokensForSelect={tokensForSelect || []}
          onTokenSelect={(t) => onTokenSelect?.(t)}
          onClose={() => setTokenPickerVisible(false)}
          preselectChain={preselectChain}
          tokensToBalances={tokensToBalances}
        />
      )}
    </Surface>
  )
}

interface Props extends PropsWithChildren {
  fromToken?: PlugToken
  toToken?: PlugToken
  setToToken: (id: string | undefined) => void
  setFromToken: (id: string | undefined) => void
  amount?: string
  setAmount: (amount: string) => void
  toAmount?: string
  setToAmount: (amount: string) => void
  maxAmount?: bigint
  priceHuman?: string
  error?: InputError
  currentQuote?: SwapProviderQuote
  tokens: PlugToken[]
  nextTitle: string
  handleSubmit: () => void
  formattedFee?: string[]
  priceDifference?: PriceDifference
  lockToToken?: boolean
  lockFromToken?: boolean
  sourceTokens?: PlugToken[]
}

const ExchangeView: FC<Props> = ({
  toToken,
  setFromToken,
  fromToken,
  setToToken,
  amount,
  setAmount,
  toAmount,
  setToAmount,
  maxAmount,
  priceHuman,
  error,
  currentQuote,
  tokens,
  nextTitle,
  handleSubmit,
  children,
  formattedFee,
  priceDifference,
  lockToToken,
  lockFromToken,
  sourceTokens,
}) => {
  const [isSwitched, setIsSwitched] = useState(false)
  const [swapProviderSelectVisible, setSwapProviderSelectVisible] = useState(false)

  const { evmChains } = useConfig()
  const { tokensToBalances } = useBalances()

  const switchTokens = () => {
    setIsSwitched((prevstate) => !prevstate)

    setFromToken(toToken?.id)
    setToToken(fromToken?.id)
    setAmount('')
  }

  const filteredDestinationTokens = useMemo(
    () => filterDestinationTokens(fromToken, tokens, evmChains),
    [fromToken?.id, evmChains]
  )

  const renderSwitchButton = () => {
    return (
      <div className="relative z-10 -my-4 flex justify-center">
        <IconButton
          aria-label="Switch tokens"
          variant="secondary"
          onClick={switchTokens}
          className="bg-sheet shadow-sm"
        >
          <HiOutlineSwitchVertical
            aria-hidden="true"
            className={cn(
              'transition-transform duration-100 ease-in-out motion-reduce:transition-none',
              isSwitched ? 'rotate-0' : 'rotate-180'
            )}
          />
        </IconButton>
      </div>
    )
  }

  const valueText = (value: React.ReactNode) => (
    <Text as="span" variant="body-s" tone="fg-2" tabular>
      {value}
    </Text>
  )

  const renderOptions = () => {
    return (
      <ListGroup>
        <ListRow
          dense
          disabled={!currentQuote}
          onClick={() => setSwapProviderSelectVisible(true)}
          primary={<span className="text-[14px] font-normal">Provider</span>}
          trailing={valueText(currentQuote?.provider)}
          chevron
        />

        <ListRow
          dense
          primary={<span className="text-[14px] font-normal">Price</span>}
          trailing={valueText(priceHuman)}
        />

        {currentQuote &&
          !!formattedFee?.length &&
          formattedFee.map((f, i) => (
            <ListRow
              key={i}
              dense
              primary={
                <span className="text-[14px] font-normal">{i === 0 ? 'Network fee' : ''}</span>
              }
              trailing={valueText(f)}
            />
          ))}

        {typeof currentQuote?.slippage === 'number' && (
          <ListRow
            dense
            primary={<span className="text-[14px] font-normal">Slippage</span>}
            trailing={valueText(
              formatNumber(currentQuote.slippage, '%', { maximumFractionDigits: 2 })
            )}
          />
        )}
      </ListGroup>
    )
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-col gap-1">
          <TokenCard
            token={fromToken}
            onTokenSelect={(token) => setFromToken(token?.id)}
            showBalance
            amount={amount}
            maxAmount={maxAmount}
            setAmount={setAmount}
            tokensForSelect={sourceTokens ?? tokens}
            error={error?.inputFieldError}
            direction="from"
            disableTokenPicker={lockFromToken}
            tokensToBalances={tokensToBalances}
          />
          {!lockToToken && !lockFromToken && renderSwitchButton()}
          <TokenCard
            token={toToken}
            onTokenSelect={(token) => setToToken(token?.id)}
            amount={toAmount}
            setAmount={setToAmount}
            tokensForSelect={filteredDestinationTokens}
            direction="to"
            disableTokenPicker={lockToToken}
            preselectChain={fromToken?.chain?.toString()}
            priceDifference={priceDifference}
            disablePriceInput
            tokensToBalances={tokensToBalances}
          />
        </div>

        {renderOptions()}

        <Button variant="primary" disabled={!!error} onClick={handleSubmit} fullWidth>
          {error?.buttonError || nextTitle}
        </Button>

        <SwapProviderSelect
          visible={swapProviderSelectVisible}
          onClose={() => setSwapProviderSelectVisible(false)}
        />
      </div>
      {children}
    </div>
  )
}

export default ExchangeView
